import React, { useMemo, useState } from 'react';
import axios from 'axios';
import styles from './DEGAnalysisModal.module.css';
import { useTranslation } from 'react-i18next'; // ★ 추가

function DEGAnalysisModal({ onClose, selectedFiles, activeTab, rnaList, userName }) {
    const { t } = useTranslation(); // ★ 추가

    // 검색어 상태 관리
    const [normalSearch, setNormalSearch] = useState('');
    const [tumorSearch, setTumorSearch] = useState('');

    // 선택된 샘플 리스트 상태 관리 (sampleName 문자열 배열)
    const [selectedNormal, setSelectedNormal] = useState([]);
    const [selectedTumor, setSelectedTumor] = useState([]);

    // 1. 현재 탭(Project ID)에 해당하는 샘플만 필터링
    const filteredByTab = useMemo(() => {
        if (!Array.isArray(rnaList)) return [];
        return rnaList.filter((item) => {
            if (!item.sampleName || item.sampleName.length < 3) return false;
            // 샘플명 3번째 글자(index 2)가 탭 번호와 일치하는지 확인
            const tabChar = item.sampleName.charAt(2);
            const tabNum = parseInt(tabChar, 10);
            return tabNum === activeTab;
        });
    }, [rnaList, activeTab]);

    // 2. Normal 그룹 필터링 (8번째 글자가 'N') + 검색어 적용
    const normalList = useMemo(() => {
        return filteredByTab.filter((item) => {
            if (!item.sampleName || item.sampleName.length < 8) return false;
            const groupChar = item.sampleName.charAt(7).toUpperCase();
            
            // N이 아니면 제외
            if (groupChar !== 'N') return false;

            // 검색어가 있으면 검색어 포함 여부 확인
            if (!normalSearch) return true;
            return item.sampleName.toLowerCase().includes(normalSearch.toLowerCase());
        });
    }, [filteredByTab, normalSearch]);

    // 3. Tumor 그룹 필터링 (8번째 글자가 'T') + 검색어 적용
    const tumorList = useMemo(() => {
        return filteredByTab.filter((item) => {
            if (!item.sampleName || item.sampleName.length < 8) return false;
            const groupChar = item.sampleName.charAt(7).toUpperCase();
            
            // T가 아니면 제외
            if (groupChar !== 'T') return false;

            // 검색어가 있으면 검색어 포함 여부 확인
            if (!tumorSearch) return true;
            return item.sampleName.toLowerCase().includes(tumorSearch.toLowerCase());
        });
    }, [filteredByTab, tumorSearch]);

    // Normal 체크박스 토글 함수
    const toggleNormal = (sampleName) => {
        setSelectedNormal((prev) =>
            prev.includes(sampleName)
                ? prev.filter((name) => name !== sampleName)
                : [...prev, sampleName]
        );
    };

    // Tumor 체크박스 토글 함수
    const toggleTumor = (sampleName) => {
        setSelectedTumor((prev) =>
            prev.includes(sampleName)
                ? prev.filter((name) => name !== sampleName)
                : [...prev, sampleName]
        );
    };

    // [핵심 수정] 분석 실행 버튼 핸들러
    const handleRunDEG = async () => {
        // 유효성 검사 강화: 각 그룹당 최소 2개 이상 선택 필수
        if (selectedNormal.length < 2 || selectedTumor.length < 2) {
            alert(t('분석 불가\n\n정확한 비교 분석과 통계적 유의성 확보를 위해\nNormal과 Tumor 샘플을 각각 "최소 2개 이상" 선택해주세요.'));
            return;
        }

        // 전체 샘플 리스트 병합
        const allSamples = [...selectedNormal, ...selectedTumor];

        // 백엔드로 보낼 데이터 구성
        const payload = {
            samples: allSamples,
            projectId: activeTab, // 필요 시 프로젝트 ID 전송
            userName: userName    // 로그인한 사용자 이름 전송
        };

        console.log('DEG 분석 요청 payload:', payload);

        try {
            // 백엔드 API 호출
            const response = await axios.post('/api/analyze/deg', payload);

            if (response.status === 200) {
                // 이전 대화에서 추가했던 번역 키 사용
                alert(t('DEG 분석이 시작되었습니다.\n\n잠시 후 "파일 목록" 팝업창에서 결과 파일을 확인 및 다운로드하실 수 있습니다.'));
                onClose(); // 성공 시 모달 닫기
            }
        } catch (error) {
            console.error('DEG 분석 요청 실패:', error);
            
            if (error.response && error.response.data && error.response.data.error) {
                // 고정 텍스트만 번역
                alert(`${t('오류 발생:')} ${error.response.data.error}`);
            } else {
                alert(t('분석 요청 중 서버 오류가 발생했습니다.'));
            }
        }
    };

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                {/* 헤더 영역 */}
                <div className={styles.header}>
                    <h2>{t('DEG 분석')}</h2>
                    <button className={styles.closeBtn} onClick={onClose}>
                        ×
                    </button>
                </div>

                {/* 본문 영역 */}
                <div className={styles.body}>
                    <div className={styles.columns}>
                        
                        {/* 왼쪽 컬럼: Normal */}
                        <div className={styles.column}>
                            <div className={styles.columnHeader}>
                                <span className={styles.columnTitle}>
                                    Normal ({t('선택:')} {selectedNormal.length})
                                </span>
                                <input
                                    type="text"
                                    className={styles.searchInput}
                                    placeholder={t('Normal 샘플 검색')}
                                    value={normalSearch}
                                    onChange={(e) => setNormalSearch(e.target.value)}
                                />
                            </div>
                            <div className={styles.list}>
                                {normalList.map((item) => (
                                    <label key={item.sampleName} className={styles.listItem}>
                                        <input
                                            type="checkbox"
                                            className={styles.checkbox}
                                            checked={selectedNormal.includes(item.sampleName)}
                                            onChange={() => toggleNormal(item.sampleName)}
                                        />
                                        <span className={styles.sampleName}>
                                            {item.sampleName}
                                        </span>
                                    </label>
                                ))}
                                {normalList.length === 0 && (
                                    <div className={styles.emptyText}>
                                        {t('표시할 Normal 샘플이 없습니다.')}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* 오른쪽 컬럼: Tumor */}
                        <div className={styles.column}>
                            <div className={styles.columnHeader}>
                                <span className={styles.columnTitle}>
                                    Tumor ({t('선택:')} {selectedTumor.length})
                                </span>
                                <input
                                    type="text"
                                    className={styles.searchInput}
                                    placeholder={t('Tumor 샘플 검색')}
                                    value={tumorSearch}
                                    onChange={(e) => setTumorSearch(e.target.value)}
                                />
                            </div>
                            <div className={styles.list}>
                                {tumorList.map((item) => (
                                    <label key={item.sampleName} className={styles.listItem}>
                                        <input
                                            type="checkbox"
                                            className={styles.checkbox}
                                            checked={selectedTumor.includes(item.sampleName)}
                                            onChange={() => toggleTumor(item.sampleName)}
                                        />
                                        <span className={styles.sampleName}>
                                            {item.sampleName}
                                        </span>
                                    </label>
                                ))}
                                {tumorList.length === 0 && (
                                    <div className={styles.emptyText}>
                                        {t('표시할 Tumor 샘플이 없습니다.')}
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>
                </div>

                {/* 푸터 영역 */}
                <div className={styles.footer}>
                    <div className={styles.summary}>
                        {t('총')} {selectedNormal.length + selectedTumor.length}{t('개 샘플 선택됨')}
                    </div>
                    <button
                        className={styles.actionBtn}
                        type="button"
                        onClick={handleRunDEG}
                        // 조건 불충족 시에도 클릭하여 경고창을 띄우기 위해 disabled 제거함
                    >
                        {t('분석 실행')}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default DEGAnalysisModal;